import React, { useEffect, useState, useCallback, useRef } from 'react';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import { FunnelIcon, SearchIcon, DownloadIcon, InboxIcon, AlertTriangleIcon, XCircleIcon } from '../components/Icon';
import { api } from '../utils/api';
import { getCurrentUser } from '../utils/auth';
import { can } from '../utils/permissions';
import { SALES_FIELDS, ORIGINAL_FIELDS, parseSalesDetails, isFieldVisible } from '../utils/salesFields';
import { downloadSalesAuditPdf } from '../utils/salesPdf';
import * as XLSX from 'xlsx';

const SOURCE_OPTIONS = ['Digital Marketing', 'Walk In', 'Channel Partner', 'Reference'];

const SOURCE_COLORS = {
  'Digital Marketing': { bg: '#ebf5fb', color: '#2980b9', border: '#2980b9' },
  'Walk In':           { bg: '#eafaf1', color: '#27ae60', border: '#27ae60' },
  'Channel Partner':   { bg: '#fef5e7', color: '#e67e22', border: '#e67e22' },
  'Reference':         { bg: '#f5eef8', color: '#8e44ad', border: '#8e44ad' },
};

const DEFAULT_FILTERS = { from: '', to: '', project: '', source: '', search: '', page: 1, limit: 20 };

// Activity-log event catalog (gp_visitor_history): badge colours + labels.
const HISTORY_EVENTS = {
  picked:     { label: 'Picked',          bg: '#eafaf1', color: '#1e8449', border: '#a9dfbf' },
  reassigned: { label: 'Reassigned',      bg: '#fef5e7', color: '#b9770e', border: '#f5cba7' },
  unassigned: { label: 'Unassigned',      bg: '#fdedec', color: '#c0392b', border: '#f1948a' },
  submitted:  { label: 'Audit Submitted', bg: '#ebf5fb', color: '#2980b9', border: '#aed6f1' },
  edited:     { label: 'Re-edited',       bg: '#f5eef8', color: '#8e44ad', border: '#d7bde2' },
};

function formatDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(val) {
  if (!val) return '—';
  const d = new Date(String(val).replace(' ', 'T'));
  if (isNaN(d.getTime())) return val;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function SourceBadge({ source }) {
  if (!source) return <span className="tbl-dash">—</span>;
  const s = SOURCE_COLORS[source];
  if (!s) return <span>{source}</span>;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap',
    }}>
      {source}
    </span>
  );
}

function WaBadge({ status }) {
  if (!status || status === '—') return <span className="tbl-dash">—</span>;
  const variant = status === 'Sent' ? 'approved' : status === 'Failed' ? 'rejected' : 'default';
  return <span className={`badge badge--${variant}`}>{status}</span>;
}

// True once the salesperson has saved Sales Audit follow-up details for a visitor.
function isSalesUpdated(r) {
  if (r.sales_updated_at && r.sales_updated_at !== '0000-00-00 00:00:00') return true;
  if (r.sales_updated_by) return true;
  if (r.sales_details && r.sales_details !== '{}' && r.sales_details !== 'null') return true;
  return false;
}

// One read-only label / value pair used inside the visitor detail modal.
function DField({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 0' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</span>
      <span style={{ fontSize: 13.5, color: '#2c2c2c', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

const DETAIL_GRID = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' };
const SECTION_HEAD = { fontWeight: 700, fontSize: 12, color: '#b8862e', textTransform: 'uppercase', letterSpacing: '.5px', margin: '16px 0 6px', borderBottom: '1px solid #f0e8d4', paddingBottom: 4 };
const SUBSECTION_HEAD = { gridColumn: '1 / -1', fontWeight: 700, fontSize: 11.5, color: '#8a6020', textTransform: 'uppercase', letterSpacing: '.4px', margin: '12px 0 2px' };

function exportVisitorsToExcel(rows) {
  const cols = [
    'Ref No', 'Name', 'Mobile', 'Project', 'Source', 'Source Description', 'Site Visit Date', 'Sales Update Date', 'Accepted By', 'Status', 'WhatsApp',
  ];
  const data = rows.map(r => [
    r.ref_number || '',
    r.customer_name || '',
    r.mobile || '',
    r.project_name || '',
    r.source || '',
    r.source_description || '',
    formatDate(r.submit_date || r.created_at),
    (isSalesUpdated(r) && r.sales_updated_at && r.sales_updated_at !== '0000-00-00 00:00:00') ? formatDate(r.sales_updated_at) : '',
    r.accepted_by_name || 'Not accepted',
    isSalesUpdated(r) ? 'Details Updated' : 'Pending Details',
    r.wa_status || '',
  ]);
  const ws = XLSX.utils.aoa_to_sheet([cols, ...data]);
  ws['!cols'] = [
    { wch: 14 }, { wch: 24 }, { wch: 14 }, { wch: 20 },
    { wch: 18 }, { wch: 28 }, { wch: 15 }, { wch: 16 }, { wch: 18 }, { wch: 15 }, { wch: 10 },
  ];
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (cell) {
      cell.s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: 'B8862E' } },
        alignment: { horizontal: 'center' },
      };
    }
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Visitors');
  XLSX.writeFile(wb, `Visitors_${new Date().toISOString().split('T')[0]}.xlsx`);
}

export default function Visitors() {
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [apiError, setApiError]     = useState('');
  const [filters, setFilters]       = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [projects, setProjects]     = useState([]);
  const [exporting, setExporting]   = useState(false);
  const [salespeople, setSalespeople] = useState([]);
  const [viewRow, setViewRow]       = useState(null);
  const drawerRef = useRef(null);

  const me = getCurrentUser();
  // Admins (and users with visitors.assign) may override who has accepted a lead.
  const canOverride = can(me, 'visitors.assign');

  useEffect(() => {
    api.projects().then(res => {
      if (res && res.projects) {
        setProjects(res.projects.filter(p => p.is_active === '1' || p.is_active === 1 || p.is_active === true));
      }
    }).catch(() => {});
  }, []);

  // Salespeople power the accept-override dropdown (admin / visitors.assign).
  useEffect(() => {
    if (!canOverride) return;
    api.salespeople().then(res => {
      if (res && Array.isArray(res.salespeople)) setSalespeople(res.salespeople);
    }).catch(() => {});
  }, [canOverride]);

  // Admin overrides who has accepted a lead: reassign (force) or clear (0).
  const handleAccept = useCallback(async (row, value) => {
    const newId = value ? Number(value) : 0;
    // Taking a lead away from someone (reassign or clear) deserves a confirm.
    if (row.accepted_by && newId !== Number(row.accepted_by)) {
      const newName = salespeople.find(s => Number(s.id) === newId)?.name;
      const msg = newId
        ? `Reassign this lead from ${row.accepted_by_name || 'current salesperson'} to ${newName || 'the selected user'}?`
        : `Clear acceptance? ${row.accepted_by_name || 'The current salesperson'} will lose this lead.`;
      if (!window.confirm(msg)) return;
    }
    const res = await api.visitorAccept(row.id, newId, true, me?.name || me?.username || '');
    if (res?.success) {
      const patch = { accepted_by: res.accepted_by, accepted_by_name: res.accepted_by_name || '' };
      setRows(rs => rs.map(r => (r.id === row.id ? { ...r, ...patch } : r)));
      setViewRow(v => (v && v.id === row.id ? { ...v, ...patch } : v));
    } else if (res?.error) {
      window.alert(res.error);
    }
  }, [salespeople, me]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => (f.search === searchText ? f : { ...f, search: searchText, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onDown = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [filterOpen]);

  const activeFilterCount = ['from', 'to', 'project', 'source', 'search'].filter(k => filters[k]).length;

  const fetchData = useCallback(async (f) => {
    setLoading(true);
    setApiError('');
    const params = {};
    if (f.from)    params.from    = f.from;
    if (f.to)      params.to      = f.to;
    if (f.project) params.project = f.project;
    if (f.source)  params.source  = f.source;
    if (f.search)  params.search  = f.search;
    params.page  = f.page;
    params.limit = f.limit;
    const res = await api.visitorList(params);
    if (res?._encrypted) {
      setApiError('Data is encrypted — set your encryption key in Settings to view records.');
      setRows([]);
      setTotal(0);
    } else if (res?.error) {
      setApiError(res.error);
      setRows([]);
      setTotal(0);
    } else {
      setRows(res.data || []);
      setTotal(res.total || 0);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(filters); }, [filters, fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.from)    params.from    = filters.from;
      if (filters.to)      params.to      = filters.to;
      if (filters.project) params.project = filters.project;
      if (filters.source)  params.source  = filters.source;
      if (filters.search)  params.search  = filters.search;
      params.page  = 1;
      params.limit = 100000;
      const res = await api.visitorList(params);
      const all = (res && res.data) || [];
      if (!all.length) return;
      exportVisitorsToExcel(all);
    } finally {
      setExporting(false);
    }
  };

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const dash = <span className="tbl-dash">—</span>;

  const columns = [
    {
      key: 'ref_number', header: 'Ref No', nowrap: true,
      tdStyle: { color: '#b8862e', fontWeight: 800, fontFamily: 'Georgia,serif' },
      render: (r) => r.ref_number || dash,
    },
    {
      key: 'customer_name', header: 'Name',
      tdStyle: { fontWeight: 600 },
      render: (r) => r.customer_name || dash,
    },
    { key: 'mobile', header: 'Mobile', render: (r) => r.mobile || dash },
    { key: 'project_name', header: 'Project', render: (r) => r.project_name || dash },
    {
      key: 'source', header: 'Source', align: 'center',
      render: (r) => <SourceBadge source={r.source} />,
    },
    {
      key: 'source_description', header: 'Source Description',
      tdStyle: { fontSize: 12, color: '#555' },
      render: (r) => r.source_description || dash,
    },
    {
      key: 'date', header: 'Site Visit Date', nowrap: true,
      tdStyle: { fontSize: 11.5, color: '#aaa' },
      render: (r) => formatDate(r.submit_date || r.created_at),
    },
    {
      key: 'sales_updated_at', header: 'Sales Update Date', nowrap: true,
      tdStyle: { fontSize: 11.5, color: '#aaa' },
      render: (r) => (isSalesUpdated(r) && r.sales_updated_at && r.sales_updated_at !== '0000-00-00 00:00:00'
        ? formatDate(r.sales_updated_at)
        : dash),
    },
    {
      key: 'accepted_by', header: 'Accepted By', nowrap: true,
      render: (r) => {
        if (!canOverride) {
          return r.accepted_by_name
            ? <span className="badge badge--approved">{r.accepted_by_name}</span>
            : <span className="tbl-dash">Not accepted</span>;
        }
        // Keep the current holder visible even if they no longer appear in the
        // salespeople list (deactivated / permission revoked).
        const inList = !r.accepted_by || salespeople.some(s => Number(s.id) === Number(r.accepted_by));
        return (
          <select
            value={r.accepted_by ? String(r.accepted_by) : ''}
            onChange={e => handleAccept(r, e.target.value)}
            onClick={e => e.stopPropagation()}
            title="Assign / reassign this lead"
            style={{ padding: '4px 6px', fontSize: 12, border: '1px solid #d9cba3', borderRadius: 6, background: '#fff', maxWidth: 160 }}
          >
            <option value="">— Not accepted —</option>
            {!inList && (
              <option value={String(r.accepted_by)}>{r.accepted_by_name || `User #${r.accepted_by}`}</option>
            )}
            {salespeople.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>
        );
      },
    },
    {
      key: 'sales_status', header: 'Status', align: 'center', nowrap: true,
      render: (r) => {
        const updated = isSalesUpdated(r);
        return (
          <span className={`badge badge--${updated ? 'approved' : 'default'}`}>
            {updated ? 'Details Updated' : 'Pending Details'}
          </span>
        );
      },
    },
    {
      key: 'wa_status', header: 'WhatsApp', align: 'center',
      render: (r) => <WaBadge status={r.wa_status} />,
    },
    {
      key: '_view', header: '', align: 'center', nowrap: true,
      render: (r) => (
        <button
          type="button"
          className="subs-filter-btn"
          style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); setViewRow(r); }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Site Visit Records</h1>
          <div className="page-sub">{total} total records</div>
        </div>
        <div className="subs-btn-group">
          <div className="subs-search">
            <SearchIcon size={15} />
            <input
              type="text"
              className="subs-search-input"
              placeholder="Search name / mobile / ref…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {searchText && (
              <button className="subs-search-clear" onClick={() => setSearchText('')} title="Clear search" aria-label="Clear search">×</button>
            )}
          </div>

          <button className="subs-filter-btn" onClick={handleExport} disabled={exporting} title="Export to Excel">
            <DownloadIcon size={15} /> <span>{exporting ? 'Exporting…' : 'Export'}</span>
          </button>

          <button className="subs-filter-btn" onClick={() => setFilterOpen(o => !o)}>
            <FunnelIcon size={15} /> <span>Filter</span>
            {activeFilterCount > 0 && <span className="subs-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      <>
        <div className={`drawer-overlay${filterOpen ? ' is-open' : ''}`} onClick={() => setFilterOpen(false)} />
        <aside className={`drawer${filterOpen ? ' is-open' : ''}`} ref={drawerRef} role="dialog" aria-label="Filters" aria-hidden={!filterOpen}>
          <div className="drawer-header">
            <span className="drawer-title"><FunnelIcon size={16} /> Filters</span>
            <button className="drawer-close" onClick={() => setFilterOpen(false)} aria-label="Close filters">×</button>
          </div>
          <div className="drawer-body">
            <div className="drawer-field">
              <label className="fb-label">Search</label>
              <input
                type="text" className="fb-input"
                placeholder="Name / Mobile / Ref No…"
                value={filters.search || ''}
                onChange={e => setFilter('search', e.target.value)}
              />
            </div>
            <div className="drawer-field">
              <label className="fb-label">Date From</label>
              <input type="date" className="fb-input" value={filters.from || ''} onChange={e => setFilter('from', e.target.value)} />
            </div>
            <div className="drawer-field">
              <label className="fb-label">Date To</label>
              <input type="date" className="fb-input" value={filters.to || ''} onChange={e => setFilter('to', e.target.value)} />
            </div>
            <div className="drawer-field">
              <label className="fb-label">Project</label>
              <select className="fb-input" value={filters.project || ''} onChange={e => setFilter('project', e.target.value)}>
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.code_name}>{p.code_name}</option>
                ))}
              </select>
            </div>
            <div className="drawer-field">
              <label className="fb-label">Source</label>
              <select className="fb-input" value={filters.source || ''} onChange={e => setFilter('source', e.target.value)}>
                <option value="">All Sources</option>
                {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="drawer-footer">
            <button className="fb-reset" onClick={() => { setFilters(DEFAULT_FILTERS); setSearchText(''); }}>
              <XCircleIcon size={14} /> Reset
            </button>
            <button className="btn-primary" onClick={() => setFilterOpen(false)}>Done</button>
          </div>
        </aside>
      </>

      {apiError && !loading && (
        <div className="subs-error-banner">
          <div className="subs-error-title">
            <AlertTriangleIcon size={16} /> Cannot load visitors
          </div>
          <div className="subs-error-detail">{apiError}</div>
        </div>
      )}

      <div className="table-card">
        {loading ? (
          <div className="loader-wrap"><div className="spinner" /></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <div className={apiError ? 'subs-empty-icon--error' : 'subs-empty-icon'}>
              {apiError ? <AlertTriangleIcon size={42} /> : <InboxIcon size={42} />}
            </div>
            <div className="subs-empty-title">
              {apiError ? 'Could not load data' : 'No records found'}
            </div>
            <div className="subs-empty-sub">
              {apiError ? 'See error above for details' : 'Try adjusting your filters'}
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={rows}
            rowKey={(r) => r.id}
            minWidth={1600}
            maxHeight="62vh"
          />
        )}

        {!loading && !apiError && rows.length > 0 && (
          <Pagination
            page={filters.page}
            pageSize={filters.limit}
            total={total}
            onPageChange={p => setFilters(f => ({ ...f, page: p }))}
            onPageSizeChange={n => setFilters(f => ({ ...f, limit: n, page: 1 }))}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {viewRow && (
        <VisitorDetailModal
          row={viewRow}
          canOverride={canOverride}
          canHistory={can(me, 'visitors.history')}
          salespeople={salespeople}
          onAccept={handleAccept}
          onClose={() => setViewRow(null)}
        />
      )}
    </div>
  );
}

// Read-only view of a visitor + its submitted Sales Audit details, plus an
// assign/reassign control for admins and the per-record activity history
// (picks, reassigns, audit submissions, re-edits). Original visitor fields
// are never editable.
function VisitorDetailModal({ row, canOverride, canHistory, salespeople, onAccept, onClose }) {
  const sd = parseSalesDetails(row.sales_details);
  const updated = isSalesUpdated(row);

  // Per-visitor activity timeline. Re-fetched when the acceptance changes so a
  // reassign done from this very modal shows up immediately.
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  useEffect(() => {
    if (!canHistory) return;
    let alive = true;
    setHistLoading(true);
    api.visitorHistory({ visitor_id: row.id, limit: 200 })
      .then(res => { if (alive) setHistory(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => {})
      .finally(() => { if (alive) setHistLoading(false); });
    return () => { alive = false; };
  }, [row.id, row.accepted_by, canHistory]);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h2 className="modal-title"><span>Visitor · {row.ref_number || '—'}</span></h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-form" style={{ overflowY: 'auto' }}>
          {/* Acceptance */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#faf6ec', border: '1px solid #f0e8d4', borderRadius: 8, padding: '10px 14px' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '.4px' }}>Accepted By</span>
            {canOverride ? (
              <select
                value={row.accepted_by ? String(row.accepted_by) : ''}
                onChange={e => onAccept(row, e.target.value)}
                style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #d9cba3', borderRadius: 6, background: '#fff' }}
              >
                <option value="">— Not accepted —</option>
                {row.accepted_by && !salespeople.some(s => Number(s.id) === Number(row.accepted_by)) && (
                  <option value={String(row.accepted_by)}>{row.accepted_by_name || `User #${row.accepted_by}`}</option>
                )}
                {salespeople.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>{row.accepted_by_name || 'Not accepted'}</span>
            )}
            <span className={`badge badge--${updated ? 'approved' : 'default'}`} style={{ marginLeft: 'auto' }}>
              {updated ? 'Details Updated' : 'Pending Details'}
            </span>
          </div>

          {/* Visitor details — always read-only */}
          <div style={SECTION_HEAD}>Visitor Details</div>
          <div style={DETAIL_GRID}>
            {ORIGINAL_FIELDS.map(f => (
              <div key={f.key} style={f.span ? { gridColumn: '1 / -1' } : undefined}>
                <DField label={f.label} value={row[f.key]} />
              </div>
            ))}
          </div>

          {/* Sales Audit details */}
          <div style={SECTION_HEAD}>Sales Audit Details</div>
          {updated ? (
            <>
              <div style={DETAIL_GRID}>
                {SALES_FIELDS.map((f, i) => {
                  if (f.section) return <div key={`s${i}`} style={SUBSECTION_HEAD}>{f.section}</div>;
                  // Conditional field whose condition wasn't met — nothing to show.
                  if (!isFieldVisible(f, sd)) return null;
                  return (
                    <div key={f.key} style={f.span ? { gridColumn: '1 / -1' } : undefined}>
                      <DField label={f.label} value={sd[f.key]} />
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
                Last updated by {row.sales_updated_by || '—'}
                {row.sales_updated_at ? ` on ${String(row.sales_updated_at).split(' ')[0]}` : ''}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic', padding: '4px 0' }}>
              No Sales Audit details submitted yet.
            </div>
          )}

          {/* Per-record activity history */}
          {canHistory && (
            <>
              <div style={SECTION_HEAD}>Activity History</div>
              {histLoading ? (
                <div style={{ fontSize: 13, color: '#999', padding: '4px 0' }}>Loading history…</div>
              ) : history.length === 0 ? (
                <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic', padding: '4px 0' }}>
                  No activity recorded yet.
                </div>
              ) : (
                <div>
                  {history.map(h => {
                    const e = HISTORY_EVENTS[h.action] || {};
                    return (
                      <div key={h.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px dashed #f0e8d4' }}>
                        <span style={{
                          flexShrink: 0, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: e.bg || '#f4f4f4', color: e.color || '#555',
                          border: `1px solid ${e.border || '#ddd'}`, whiteSpace: 'nowrap',
                        }}>
                          {e.label || h.action}
                        </span>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#2c2c2c' }}>
                          <span style={{ fontWeight: 600 }}>{h.user_name || '—'}</span>
                          {h.details ? <span style={{ color: '#777' }}> — {h.details}</span> : null}
                        </div>
                        <span style={{ flexShrink: 0, fontSize: 11.5, color: '#aaa', whiteSpace: 'nowrap' }}>
                          {formatDateTime(h.created_at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ padding: '16px 24px 20px' }}>
          <button type="button" className="modal-cancel" onClick={onClose}>Close</button>
          <button
            type="button"
            className="modal-save"
            onClick={() => downloadSalesAuditPdf(row, row.sales_details)}
            title={updated ? 'Download the Sales Audit as PDF' : 'Sales Audit not filled yet — PDF will show visitor details only'}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <DownloadIcon size={16} />
            <span>Download PDF</span>
          </button>
        </div>
      </div>
    </div>
  );
}
